import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { agentKeys } from "../queryKeys";

interface TimeSeriesDataPoint {
  date: string;
  count: number;
}

interface AnalyticsChartProps {
  agentId: string;
}

type RangePreset = "1d" | "7d" | "1m" | "3m";

const RANGE_PRESETS: { value: RangePreset; label: string }[] = [
  { value: "1d", label: "1d" },
  { value: "7d", label: "7d" },
  { value: "1m", label: "1m" },
  { value: "3m", label: "3m" },
];

export function AnalyticsChart({ agentId }: AnalyticsChartProps) {
  const [metric, setMetric] = useState<"messages" | "conversations">("messages");
  const [range, setRange] = useState<RangePreset>("7d");
  const [groupBy, setGroupBy] = useState<"1d" | "1w" | "1m">("1d");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const { data: rawData = [], isLoading: loading } = useQuery({
    queryKey: agentKeys.timeSeries(agentId, { metric, groupBy, range }),
    queryFn: () =>
      apiFetch<{ data: TimeSeriesDataPoint[] }>(
        `/api/agents/${agentId}/stats/time-series?metric=${metric}&group_by=${groupBy}&range=${range}`
      ).then((r) => r.data),
  });
  const data = rawData;

  const total = data.reduce((sum, point) => sum + point.count, 0);
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const chartHeight = 300;
  const chartWidth = Math.max(800, data.length * 30);
  const barWidth = chartWidth / Math.max(data.length, 1);
  const padding = 40;

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (groupBy === "1d") {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } else if (groupBy === "1w") {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } else {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }
  }

  function formatTooltipDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "long", 
      day: "numeric", 
      year: "numeric",
      timeZone: "UTC"
    }) + " UTC";
  }

  function handleMouseMove(e: React.MouseEvent<SVGRectElement>, index: number) {
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setHoveredIndex(index);
      setHoverPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }

  function handleMouseLeave() {
    setHoveredIndex(null);
    setHoverPosition(null);
  }

  if (loading) {
    return (
      <div className="analytics-chart-loading">
        <p>Loading chart data...</p>
      </div>
    );
  }

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <div className="analytics-chart-title-section">
          <h3 className="analytics-chart-title">
            Total {metric === "messages" ? "Messages" : "Conversations"}
          </h3>
          <div className="analytics-chart-total">{total.toLocaleString()}</div>
        </div>
        <div className="analytics-chart-controls">
          <div className="analytics-chart-control-group">
            <label>Range:</label>
            <div className="analytics-chart-presets">
              {RANGE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`analytics-chart-preset-btn ${range === preset.value ? "active" : ""}`}
                  onClick={() => setRange(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          <div className="analytics-chart-control-group">
            <label>Metric:</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as "messages" | "conversations")}
              className="analytics-chart-select"
            >
              <option value="messages">Messages</option>
              <option value="conversations">Conversations</option>
            </select>
          </div>
          <div className="analytics-chart-control-group">
            <label>Group by:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as "1d" | "1w" | "1m")}
              className="analytics-chart-select"
            >
              <option value="1d">1d</option>
              <option value="1w">1w</option>
              <option value="1m">1m</option>
            </select>
          </div>
        </div>
      </div>

      <div className="analytics-chart-wrapper" ref={chartRef}>
        {data.length === 0 ? (
          <div className="analytics-chart-empty">
            <p>No data available for the selected period.</p>
          </div>
        ) : (
          <svg
            width="100%"
            height={chartHeight + padding * 2}
            viewBox={`0 0 ${chartWidth + padding * 2} ${chartHeight + padding * 2}`}
            preserveAspectRatio="none"
            className="analytics-chart-svg"
          >
            {/* X-axis labels */}
            {data.map((point, index) => {
              const x = padding + (index + 0.5) * barWidth;
              const shouldShow = index % Math.ceil(data.length / 8) === 0 || index === data.length - 1;
              return shouldShow ? (
                <text
                  key={`label-${index}`}
                  x={x}
                  y={chartHeight + padding + 20}
                  textAnchor="middle"
                  className="analytics-chart-axis-label"
                >
                  {formatDate(point.date)}
                </text>
              ) : null;
            })}

            {/* Bars */}
            {data.map((point, index) => {
              const barHeight = (point.count / maxCount) * chartHeight;
              const x = padding + index * barWidth + barWidth * 0.1;
              const y = padding + chartHeight - barHeight;
              const width = barWidth * 0.8;
              const isHovered = hoveredIndex === index;

              return (
                <g key={`bar-${index}`}>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={barHeight}
                    className={`analytics-chart-bar ${isHovered ? "analytics-chart-bar-hovered" : ""}`}
                    fill="#137fec"
                  />
                  <rect
                    x={x}
                    y={padding}
                    width={width}
                    height={chartHeight}
                    fill="transparent"
                    onMouseMove={(e) => handleMouseMove(e, index)}
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: "pointer" }}
                  />
                </g>
              );
            })}

            {/* Hover tooltip */}
            {hoveredIndex !== null && hoverPosition && data[hoveredIndex] && (
              <g className="analytics-chart-tooltip-group">
                <rect
                  x={hoverPosition.x - 100}
                  y={hoverPosition.y - 50}
                  width="200"
                  height="40"
                  rx="8"
                  fill="#1e293b"
                  stroke="#283039"
                  strokeWidth="1"
                />
                <text
                  x={hoverPosition.x}
                  y={hoverPosition.y - 30}
                  textAnchor="middle"
                  className="analytics-chart-tooltip-date"
                >
                  {formatTooltipDate(data[hoveredIndex].date)}
                </text>
                <text
                  x={hoverPosition.x}
                  y={hoverPosition.y - 10}
                  textAnchor="middle"
                  className="analytics-chart-tooltip-value"
                >
                  {data[hoveredIndex].count} {metric === "messages" ? "messages" : "conversations"}
                </text>
              </g>
            )}
          </svg>
        )}
      </div>
    </div>
  );
}
